//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useDebounce } from 'use-debounce';

import {
  IContentLibrarySourcesOptions,
  MediaFilterParamKey,
} from '@apis/content-library/contentLibraryApiModel';
import HR from '@components/atoms/hr/HR';
import LoadingShimmerCaption from '@components/atoms/loading-shimmer/LoadingShimmerCaption';
import { BodyText, CaptionErrorText } from '@components/atoms/text/Text';
import { ClearSelectionButton } from '@components/media-filters/ClearSelectionButton';
import { CollapsibleSection } from '@components/media-filters/CollapsibleSection';
import {
  FilterSection,
  FilterSectionTitle,
  FilterValue,
} from '@components/media-filters/filterSectionModels';
import { getSection } from '@components/media-filters/mediaFiltersUtils';
import { useContentLibraryParamsMapper } from '@components/media-filters/useContentLibraryParamsMapper';
import { useMediaLibraryLabelsInfiniteQuery } from '@components/media-filters/useMediaLibraryLabelsInfiniteQuery';
import SearchBar from '@components/molecules/search-bar/SearchBar';
import { ContentDrawerTrackingIds } from '@content-drawer/contentDrawerConstants';
import { EmptyLabels } from '@content-drawer/filters/EmptyLabels';
import {
  DEFAULT_HAS_LABELS_FILTER_SECTION,
  LabelsCheckbox,
} from '@content-drawer/filters/LabelsCheckbox';
import { LabelsPanel } from '@content-drawer/filters/LabelsPanel';
import { AllLabelsDialog } from '@content-drawer/filters/labels/AllLabelsDialog';
import { ManageLabelsDialog } from '@content-drawer/filters/labels/ManageLabelsDialog';
import { findOptionByValue } from '@content-drawer/filters/labelsUtils';
import { getIsLabelManagementEnabled } from '@content-drawer/redux/getContentDrawer';
import { getIsContentLibraryLabelsImprovementsEnabled } from '@media-library/redux/getMediaLibraryPage';
import { isDefined } from '@utils/filterPredicates';

export const CONTENT_DRAWER_LABELS_LIMIT = 5;

export const DEFAULT_LABELS_SECTION: FilterSection<string> = {
  options: [],
  paramKey: 'labels',
};

export interface LabelsFilterProps<T extends FilterValue> {
  isDisabled?: boolean;
  isInitiallyCollapsed?: boolean;
  onChange: (
    sections: Map<MediaFilterParamKey, FilterSection<FilterValue>>,
  ) => void;
  sections: Map<MediaFilterParamKey, FilterSection<T>>;
  section: FilterSection<string> | undefined;
  sources: IContentLibrarySourcesOptions;
}

export const LabelsFilter = <T extends FilterValue>({
  isDisabled,
  isInitiallyCollapsed,
  onChange,
  section = DEFAULT_LABELS_SECTION,
  sections,
  sources,
}: LabelsFilterProps<T>) => {
  const isLabelManagementEnabled = useSelector(getIsLabelManagementEnabled);
  // TODO: PD-264130 - Remove content_library_labels_improvements feature flag
  const isContentLibraryLabelsImprovementsEnabled = useSelector(
    getIsContentLibraryLabelsImprovementsEnabled,
  );

  const { filters } = useContentLibraryParamsMapper({
    sections,
    skipLabels: true,
  });
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText] = useDebounce(searchText, 300);
  const { data, error, isError, isFetching } =
    useMediaLibraryLabelsInfiniteQuery({
      query: {
        autocompleteType: 'Labels',
        filters,
        limit: CONTENT_DRAWER_LABELS_LIMIT,
        // Search using debounced text. However, when the user removes the entire search term, use an empty string to fetch data from the cache.
        prefix: searchText === '' ? searchText : debouncedSearchText,
        sources,
      },
    });

  const handleSelect = (selected: string) => {
    const options = [...section.options];
    const selectedOption = findOptionByValue(options, selected);
    const updatedSections = new Map<
      MediaFilterParamKey,
      FilterSection<FilterValue>
    >();

    if (selectedOption) {
      selectedOption.isSelected = !selectedOption.isSelected;

      updatedSections.set(section.paramKey, { ...section, options });
    } else {
      updatedSections.set(section.paramKey, {
        ...section,
        options: [
          ...options,
          {
            isSelected: true,
            label: selected,
            value: selected,
          },
        ],
      });
    }

    if (isContentLibraryLabelsImprovementsEnabled) {
      const parentSection =
        getSection(sections, 'hasLabels') ?? DEFAULT_HAS_LABELS_FILTER_SECTION;

      const parentOptions = parentSection.options.map((option) => ({
        ...option,
        isSelected: !!option.value,
      }));

      updatedSections.set('hasLabels', {
        ...parentSection,
        options: parentOptions,
      });
    }
    onChange(updatedSections);
  };

  const labels = data?.pages.flatMap((page) => page) ?? [];
  const isEmptyDataSet = !isFetching && !isError && labels.length === 0;
  const hasFullInitialLabelsSet =
    searchText.length > 0 ||
    (searchText === '' && labels.length >= CONTENT_DRAWER_LABELS_LIMIT);

  const selectLabelsSection = (
    <>
      <SearchBar
        handleClear={() => setSearchText('')}
        isDisabled={(searchText === '' && labels.length === 0) || isDisabled}
        onChange={setSearchText}
        placeholder='Find labels...'
        trackingIdPrefix={ContentDrawerTrackingIds.SEARCH_LABELS}
        value={searchText}
        visuallyHiddenLabel='Find labels'
      />
      <Wrapper>
        <div>
          <ClearSelectionButton
            handleClearSection={(options) => {
              const updatedSections = new Map<
                MediaFilterParamKey,
                FilterSection<FilterValue>
              >();

              updatedSections.set(section.paramKey, { ...section, options });
              onChange(updatedSections);
            }}
            options={section.options}
          />
        </div>
        {isFetching ? (
          <LoadingCaption />
        ) : (
          (!isEmptyDataSet || searchText === '') && (
            <LabelsPanel
              isDisabled={isDisabled}
              labels={labels}
              onCheck={handleSelect}
              options={section.options}
              selectedOptions={section.options}
            />
          )
        )}
        {isEmptyDataSet && searchText !== '' && (
          <BodyText>No results found.</BodyText>
        )}
        {isEmptyDataSet && searchText === '' && <EmptyLabels />}
        {isDefined(error) && (
          <CaptionErrorText>{error?.message}</CaptionErrorText>
        )}
        <div>
          {hasFullInitialLabelsSet && (
            <AllLabelsDialog onChange={onChange} section={section} />
          )}
        </div>
      </Wrapper>
    </>
  );

  return (
    <>
      <CollapsibleSection
        isCollapsed={isInitiallyCollapsed}
        rightComponent={isLabelManagementEnabled && <ManageLabelsDialog />}
        title={FilterSectionTitle.labels}
      >
        {isContentLibraryLabelsImprovementsEnabled ? (
          <>
            <LabelsCheckbox
              isDisabled={isDisabled}
              onChange={onChange}
              section={getSection(sections, 'hasLabels')}
              subsection={getSection(sections, 'labels')}
            />
            <LabelsSubsection>{selectLabelsSection}</LabelsSubsection>
          </>
        ) : (
          selectLabelsSection
        )}
      </CollapsibleSection>
      <HR />
    </>
  );
};

const Wrapper = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[1]};
  `,
);

const LabelsSubsection = styled.div(
  ({ theme }) => `
    padding-left: ${theme.spacings[4]};
  `,
);

const LoadingCaption = styled(LoadingShimmerCaption)(
  ({ theme }) => `
    padding-top: ${theme.spacings[3]};
  `,
);
